import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab
} from '@mui/material';
import { useSnackbar } from 'notistack';
import localidadService from '../../services/localidadService';
import ProvinciaSearch from '../../components/ProvinciaSearch';

interface LocalidadFormData {
  codigoCiudad: number;
  nombreCiudad: string;
  codigoProvincia: number;
  codigoPostal?: string;
  nombreProvincia?: string;
}

const defaultForm: LocalidadFormData = {
  codigoCiudad: 0,
  nombreCiudad: '',
  codigoProvincia: 0,
  codigoPostal: ''
};

const LocalidadForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codLocalidad } = useParams();
  const [formData, setFormData] = useState<LocalidadFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [provinciaSearchOpen, setProvinciaSearchOpen] = useState(false);

  const loadLocalidad = useCallback(async () => {
    if (!codLocalidad || codLocalidad === 'nuevo') return;
    try {
      setLoading(true);
      const localidad = await localidadService.getLocalidad(Number(codLocalidad));
      setFormData({
        codigoCiudad: localidad.codigoCiudad,
        nombreCiudad: localidad.nombreCiudad || '',
        codigoProvincia: localidad.codigoProvincia || 0,
        codigoPostal: localidad.codigoPostal,
        nombreProvincia: localidad.nombreProvincia
      });
    } catch (error) {
      enqueueSnackbar('Error al cargar la localidad', { variant: 'error' });
      navigate('/sistema/localidades');
    } finally {
      setLoading(false);
    }
  }, [codLocalidad, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codLocalidad && codLocalidad !== 'nuevo') {
      loadLocalidad();
    }
  }, [codLocalidad, loadLocalidad]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'codigoCiudad' || name === 'codigoProvincia') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleProvinciaSelect = (provincia: any) => {
    setFormData(prev => ({
      ...prev,
      codigoProvincia: provincia.codigoProvincia,
      nombreProvincia: provincia.nombreProvincia
    }));
    setProvinciaSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombreCiudad.trim()) {
        enqueueSnackbar('El nombre de la localidad es requerido', { variant: 'warning' });
        return;
      }

      if (!formData.codigoProvincia) {
        enqueueSnackbar('Debe seleccionar una provincia', { variant: 'warning' });
        return;
      }

      const payload = {
        codigoCiudad: formData.codigoCiudad,
        nombreCiudad: formData.nombreCiudad,
        codigoProvincia: formData.codigoProvincia,
        codigoPostal: formData.codigoPostal
      };

      if (codLocalidad && codLocalidad !== 'nuevo') {
        await localidadService.updateLocalidad(Number(codLocalidad), payload);
      } else {
        await localidadService.createLocalidad(payload);
      }

      enqueueSnackbar('Localidad guardada exitosamente', { variant: 'success' });
      navigate('/sistema/localidades');
    } catch (error: any) {
      console.error('Error al guardar localidad:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la localidad', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {codLocalidad && codLocalidad !== 'nuevo' ? 'Editar Localidad' : 'Nueva Localidad'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Ubicación" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField 
                    name="codigoCiudad" 
                    label="Código" 
                    type="number"
                    value={formData.codigoCiudad} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!codLocalidad && codLocalidad !== 'nuevo'}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField 
                    name="nombreCiudad" 
                    label="Nombre de Localidad" 
                    value={formData.nombreCiudad} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Provincia" 
                      value={formData.nombreProvincia || formData.codigoProvincia || ''} 
                      fullWidth 
                      required
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setProvinciaSearchOpen(true)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="codigoPostal" 
                    label="Código Postal" 
                    value={formData.codigoPostal || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/sistema/localidades')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <ProvinciaSearch
        open={provinciaSearchOpen}
        onClose={() => setProvinciaSearchOpen(false)}
        onSelect={handleProvinciaSelect}
      />
    </Box>
  );
};

export default LocalidadForm;
